/**
 * Audit logs API client
 *
 * Phase 1-5 / Plan §4.1 / Design §16 E2.
 *
 * 包装 axios apiClient 调用 admin-service /audit-logs 端点；
 * 返回类型与 admin-service ServiceResponse 形状对齐。
 */
import { apiClient } from './client';
import type { AdminRole } from '../contracts/roles';

export interface AuditLogDto {
  id: string;
  actorId: string;
  actorRole: AdminRole;
  resourceType: string;
  resourceId: string | null;
  action: string;
  method: string;
  path: string;
  statusCode: number;
  requestId: string;
  idempotencyKey: string | null;
  beforeSummary: string | null;
  afterSummary: string | null;
  crossServiceActor: string | null;
  errorMessage: string | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface ListAuditLogsFilter {
  actorId?: string;
  resourceType?: string;
  resourceId?: string;
  action?: string;
  method?: string;
  statusCode?: number;
  requestId?: string;
  startAt?: string;
  endAt?: string;
  limit?: number;
  offset?: number;
}

export interface ListAuditLogsResponse {
  items: AuditLogDto[];
  total: number;
  limit: number;
  offset: number;
}

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

/**
 * GET /audit-logs — 列表查询（分页 + 过滤）。
 */
export async function listAuditLogs(
  filter: ListAuditLogsFilter = {},
): Promise<ListAuditLogsResponse> {
  const params: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(filter)) {
    if (v !== undefined && v !== null && v !== '') params[k] = v as string | number;
  }
  const resp = await apiClient.get<ServiceResponse<ListAuditLogsResponse>>('/audit-logs', {
    params,
  });
  return resp.data.data;
}

/**
 * GET /audit-logs/:id — 单条详情。
 */
export async function getAuditLog(id: string): Promise<AuditLogDto> {
  const resp = await apiClient.get<ServiceResponse<AuditLogDto>>(`/audit-logs/${encodeURIComponent(id)}`);
  return resp.data.data;
}

/**
 * GET /audit-logs/by-request/:requestId — 按 requestId 跨服务追溯。
 */
export async function getAuditLogsByRequestId(
  requestId: string,
): Promise<{ items: AuditLogDto[]; total: number }> {
  const resp = await apiClient.get<ServiceResponse<{ items: AuditLogDto[]; total: number }>>(
    `/audit-logs/by-request/${encodeURIComponent(requestId)}`,
  );
  return resp.data.data;
}
